import { createSlice } from "@reduxjs/toolkit";
import { trackAddToCart, trackRemoveFromCart } from "../../utils/facebookPixel";

const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      return JSON.parse(savedCart);
    }
  } catch (error) {
    console.error("Failed to load cart from localStorage", error);
  }
  return {
    items: [],
    totalAmount: 0,
  };
};

const initialState = loadCartFromStorage();

const MAX_QUANTITY = 20;

const saveCartToStorage = (items, totalAmount) => {
  try {
    localStorage.setItem('cart', JSON.stringify({ items, totalAmount }));
  } catch (error) {
    console.error("Failed to save cart to localStorage", error);
  }
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const { product, selectedSize, selectedColor, quantity = 1 } = action.payload;

      // Create a unique key for the cart item based on product, size, and color
      const itemKey = `${product.id}-${selectedSize || 'default'}-${selectedColor || 'default'}`;

      const existingItem = state.items.find(
        (item) => item.itemKey === itemKey
      );

      if (existingItem) {
        if (existingItem.quantity < MAX_QUANTITY) {
          existingItem.quantity += quantity;
          state.totalAmount += product.price * quantity;
        }
      } else {
        const cartItem = {
          ...product,
          itemKey,
          selectedSize: selectedSize || null,
          selectedColor: selectedColor || null,
          quantity: quantity,
          // Map API product fields to frontend fields
          productID: product.id,
          productName: product.name,
          productPrice: product.price,
          frontImg: product.image_url,
        };

        state.items.push(cartItem);
        state.totalAmount += product.price * quantity;

        // Track Facebook Pixel event
        trackAddToCart(product, quantity);
      }

      saveCartToStorage(state.items, state.totalAmount);
    },
    updateQuantity(state, action) {
      const { itemKey, quantity } = action.payload;
      const itemToUpdate = state.items.find(
        (item) => item.itemKey === itemKey
      );

      if (itemToUpdate) {
        const difference = quantity - itemToUpdate.quantity;
        if (quantity <= MAX_QUANTITY && quantity > 0) {
          itemToUpdate.quantity = quantity;
          state.totalAmount += difference * itemToUpdate.productPrice;
        } else if (quantity > MAX_QUANTITY) {
          const oldQuantity = itemToUpdate.quantity;
          itemToUpdate.quantity = MAX_QUANTITY;
          state.totalAmount += (MAX_QUANTITY - oldQuantity) * itemToUpdate.productPrice;
        }

        saveCartToStorage(state.items, state.totalAmount);
      }
    },
    removeFromCart(state, action) {
      const itemKey = action.payload;
      const itemToRemove = state.items.find(
        (item) => item.itemKey === itemKey
      );

      if (itemToRemove) {
        state.totalAmount -= itemToRemove.productPrice * itemToRemove.quantity;
        state.items = state.items.filter(
          (item) => item.itemKey !== itemKey
        );

        // Track Facebook Pixel event
        trackRemoveFromCart(itemToRemove, itemToRemove.quantity);

        saveCartToStorage(state.items, state.totalAmount);
      }
    },
    clearCart(state) {
      state.items = [];
      state.totalAmount = 0;
      saveCartToStorage(state.items, state.totalAmount);
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items;
export const selectCartTotalAmount = (state) => state.cart.totalAmount;

export default cartSlice.reducer;
